import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { workgroupsKeys } from '@/hooks/use-workgroups';
import {
  useWorkgroupsUIStore,
  WORKGROUPS_PRIVATE_REASON_MIN_LEN,
} from '@/stores/workgroups-ui-store';
import { isInternalRole } from '@/types';

const STORAGE_KEY_PATTERN = /storagePath|objectKey|storageObjectKey|r2Key|signedUrl/i;

function payloadHasStorageKeys(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  return Object.keys(payload as Record<string, unknown>).some((k) => STORAGE_KEY_PATTERN.test(k));
}

export interface WorkgroupsRealtimeState {
  enabled: boolean;
  connected: boolean;
  subscribed: boolean;
  error: string | null;
}

/**
 * Subscribes to `workgroup:{workGroupId}` via `/workgroups` Socket.IO namespace.
 * Requires auth, internal role, and `workgroups.view`.
 */
export function useWorkgroupsRealtime(workGroupId: string | undefined): WorkgroupsRealtimeState {
  const { user, isAuthenticated } = useAuth();
  const { hasCapability } = usePermissions();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const subscribedIdRef = useRef<string | null>(null);

  const [connected, setConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const privateAccessReason = useWorkgroupsUIStore((s) => {
    if (!workGroupId) return undefined;
    const trimmed = s.privateAccessByWorkGroupId[workGroupId]?.trim() ?? '';
    return trimmed.length >= WORKGROUPS_PRIVATE_REASON_MIN_LEN ? trimmed : undefined;
  });

  const enabled =
    isAuthenticated &&
    !!user &&
    isInternalRole(user.role) &&
    hasCapability('workgroups.view') &&
    !!workGroupId;

  useEffect(() => {
    if (!enabled || !workGroupId) {
      setConnected(false);
      setSubscribed(false);
      setError(null);
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setError('no_token');
      return;
    }

    const wsBase = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || '';
    const socketUrl = wsBase ? `${wsBase}/workgroups` : '/workgroups';

    const socket = io(socketUrl, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    const subscribe = () => {
      socket.emit(
        'subscribeWorkGroup',
        { workGroupId, privateAccessReason },
        (ack: { success?: boolean; reason?: string }) => {
          if (ack?.success) {
            subscribedIdRef.current = workGroupId;
            setSubscribed(true);
            setError(null);
          } else {
            setSubscribed(false);
            setError(ack?.reason ?? 'subscribe_failed');
          }
        },
      );
    };

    const onWorkGroupUpdated = (payload: { scope?: string } & Record<string, unknown>) => {
      if (payloadHasStorageKeys(payload)) return;
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.detail(workGroupId) });
      if (payload?.scope === 'members') {
        void queryClient.invalidateQueries({ queryKey: workgroupsKeys.members(workGroupId) });
      }
      if (payload?.scope === 'quota') {
        void queryClient.invalidateQueries({ queryKey: workgroupsKeys.meQuota });
        void queryClient.invalidateQueries({ queryKey: workgroupsKeys.adminQuotas });
      }
    };

    const onFolderUpdated = (payload: { scope?: string; folderId?: string } & Record<string, unknown>) => {
      if (payloadHasStorageKeys(payload)) return;
      void queryClient.invalidateQueries({ queryKey: ['workgroups', workGroupId, 'contents'] });
      if (payload?.scope === 'permissions' && payload.folderId) {
        void queryClient.invalidateQueries({
          queryKey: workgroupsKeys.folderPermissions(workGroupId, payload.folderId),
        });
      }
    };

    const onFileUpdated = (payload: { fileId?: string } & Record<string, unknown>) => {
      if (payloadHasStorageKeys(payload)) return;
      void queryClient.invalidateQueries({ queryKey: ['workgroups', workGroupId, 'contents'] });
      if (payload?.fileId) {
        void queryClient.invalidateQueries({
          queryKey: workgroupsKeys.fileVersions(workGroupId, payload.fileId),
        });
      }
    };

    const onTrashUpdated = (payload: { freedBytes?: string } & Record<string, unknown>) => {
      if (payloadHasStorageKeys(payload)) return;
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(workGroupId) });
      void queryClient.invalidateQueries({ queryKey: ['workgroups', workGroupId, 'contents'] });
      if (payload?.freedBytes != null) {
        void queryClient.invalidateQueries({ queryKey: workgroupsKeys.meQuota });
        void queryClient.invalidateQueries({ queryKey: workgroupsKeys.adminQuotas });
      }
    };

    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('connected', () => {
      subscribe();
    });

    socket.on('disconnect', () => {
      setConnected(false);
      setSubscribed(false);
    });

    socket.on('error', (payload: { message?: string }) => {
      setError(payload?.message ?? 'socket_error');
    });

    socket.on('workgroups.workgroup.updated', onWorkGroupUpdated);
    socket.on('workgroups.folder.updated', onFolderUpdated);
    socket.on('workgroups.file.updated', onFileUpdated);
    socket.on('workgroups.trash.updated', onTrashUpdated);

    return () => {
      const id = subscribedIdRef.current ?? workGroupId;
      if (socket.connected && id) {
        socket.emit('unsubscribeWorkGroup', { workGroupId: id });
      }
      subscribedIdRef.current = null;
      socket.off('connect');
      socket.off('connected');
      socket.off('disconnect');
      socket.off('error');
      socket.off('workgroups.workgroup.updated', onWorkGroupUpdated);
      socket.off('workgroups.folder.updated', onFolderUpdated);
      socket.off('workgroups.file.updated', onFileUpdated);
      socket.off('workgroups.trash.updated', onTrashUpdated);
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      setConnected(false);
      setSubscribed(false);
    };
  }, [enabled, workGroupId, privateAccessReason, queryClient]);

  return { enabled, connected, subscribed, error };
}
